// src/pages/ExpensePurchaseOrder/EpoLineSearch.js
//
// Batch 3 / Round 7 — EPO line-item search for Shipping / Find EPO.
//
// Lets shipping find the likely EPO for a package by searching LINE
// DESCRIPTIONS (plus vendor, creator, date range, received/paid status) when
// the slip has no EPO number — e.g. search "wedge hex" to see every EPO with a
// matching line. The search is BACKEND-DRIVEN and PAGED
// (GET /api/epo-query/line-search); we never load all EPOs into the browser.
//
// Results open in the shared Review EPO modal (on the Line Items tab) so the
// shipper can receive straight from a result without losing their search.
//
// Access: route gated by the `epo.lineSearch` module; the API re-checks
// CanSearchEpoLines (Find/Shipping permission OR IT-Admin).

import React, { useState, useCallback } from "react";
import {
    Container, Row, Col, Card, CardBody, Table, Button, Badge, Spinner, Alert, Input, Label
} from "reactstrap";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";
import api from "../../api";
import { epoErrorMessage } from "./epoErrors";  // Batch 5 / Round 13

const pick = (o, ...keys) => { for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };

const lineStatusBadge = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "received") return <Badge color="success">Received</Badge>;
    if (v === "partial") return <Badge color="warning">Partial</Badge>;
    return <Badge color="secondary">Pending</Badge>;
};

const EpoLineSearch = () => {
    const epoReview = useEpoReviewModal({
        permissions: { canReceive: true },   // controls which buttons SHOW; server still enforces
    });

    const [filters, setFilters] = useState({
        description: "", vendor: "", epoNumber: "", createdBy: "",
        dateFrom: "", dateTo: "", recvStatus: "all", paidStatus: "all",
    });
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    const runSearch = useCallback(async (goPage = 1) => {
        setLoading(true); setError(null);
        try {
            const params = {
                description: filters.description || undefined,
                vendor: filters.vendor || undefined,
                epoNumber: filters.epoNumber || undefined,
                createdBy: filters.createdBy || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                recvStatus: filters.recvStatus !== "all" ? filters.recvStatus : undefined,
                paidStatus: filters.paidStatus !== "all" ? filters.paidStatus : undefined,
                page: goPage,
                pageSize,
            };
            const res = await api.get("/epo-query/line-search", { params });
            const data = res.data || {};
            setRows(data.Items ?? data.items ?? []);
            setTotal(data.Total ?? data.total ?? 0);
            setPage(data.Page ?? data.page ?? goPage);
            setSearched(true);
        } catch (e) {
            setError(epoErrorMessage(e, "Search failed.", {
                403: "You don't have permission to search EPO line items.",
            }));
            setRows([]); setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters, pageSize]);

    const onKey = (e) => { if (e.key === "Enter") runSearch(1); };

    const clearAll = () => {
        setFilters({ description: "", vendor: "", epoNumber: "", createdBy: "", dateFrom: "", dateTo: "", recvStatus: "all", paidStatus: "all" });
        setRows([]); setTotal(0); setSearched(false); setError(null);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-search-eye-line"
                    eyebrow="EPO"
                    title="Find EPO by Item"
                    subtitle="Search line-item descriptions to locate the right EPO when a package has no EPO number."
                    tone="violet"
                    breadcrumb={[
                        { label: "Dashboard", to: "/" },
                        { label: "EPO", to: "/workflows?wf=epo" },
                        { label: "Find by Item" },
                    ]}
                />

                {/* Filters */}
                <Card className="mt-3">
                    <CardBody>
                        <Row className="g-3">
                            <Col md={4}>
                                <Label className="form-label small mb-1">Description contains</Label>
                                <Input value={filters.description} onChange={(e) => set("description", e.target.value)}
                                    onKeyDown={onKey} placeholder='e.g. "wedge hex"' />
                            </Col>
                            <Col md={4}>
                                <Label className="form-label small mb-1">Vendor / company contains</Label>
                                <Input value={filters.vendor} onChange={(e) => set("vendor", e.target.value)}
                                    onKeyDown={onKey} placeholder="e.g. Amazon" />
                            </Col>
                            <Col md={2}>
                                <Label className="form-label small mb-1">EPO #</Label>
                                <Input value={filters.epoNumber} onChange={(e) => set("epoNumber", e.target.value)}
                                    onKeyDown={onKey} placeholder="71343" />
                            </Col>
                            <Col md={2}>
                                <Label className="form-label small mb-1">Created by</Label>
                                <Input value={filters.createdBy} onChange={(e) => set("createdBy", e.target.value)}
                                    onKeyDown={onKey} placeholder="name" />
                            </Col>

                            <Col md={2}>
                                <Label className="form-label small mb-1">Date from</Label>
                                <Input type="date" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} />
                            </Col>
                            <Col md={2}>
                                <Label className="form-label small mb-1">Date to</Label>
                                <Input type="date" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Label className="form-label small mb-1">Received status</Label>
                                <Input type="select" value={filters.recvStatus} onChange={(e) => set("recvStatus", e.target.value)}>
                                    <option value="all">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="partial">Partial</option>
                                    <option value="received">Received</option>
                                </Input>
                            </Col>
                            <Col md={2}>
                                <Label className="form-label small mb-1">Paid status</Label>
                                <Input type="select" value={filters.paidStatus} onChange={(e) => set("paidStatus", e.target.value)}>
                                    <option value="all">All</option>
                                    <option value="paid">Paid</option>
                                    <option value="unpaid">Unpaid</option>
                                </Input>
                            </Col>
                            <Col md={3} className="d-flex align-items-end gap-2">
                                <Button color="primary" onClick={() => runSearch(1)} disabled={loading}>
                                    {loading ? <><Spinner size="sm" className="me-1" />Searching…</> : <><i className="ri-search-line align-bottom me-1" />Search</>}
                                </Button>
                                <Button color="light" onClick={clearAll} disabled={loading}>Clear</Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {error && <Alert color="danger" className="mt-3" toggle={() => setError(null)}>{error}</Alert>}

                {/* Results */}
                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Results</h5>
                            {searched && <span className="text-muted small">{total} matching line{total === 1 ? "" : "s"}</span>}
                        </div>

                        {!searched ? (
                            <div className="text-muted">Enter a search above. Tip: searching a partial description like “wedge” finds every EPO line that mentions it.</div>
                        ) : rows.length === 0 ? (
                            <div className="text-muted">No matching line items.</div>
                        ) : (
                            <>
                                <div className="table-responsive epo-sticky-head">
                                    <Table size="sm" hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>EPO #</th><th>Created</th><th>Created By</th><th>Vendor</th>
                                                <th>Line Description</th>
                                                <th className="text-end">Qty Ord</th><th className="text-end">Qty Rcvd</th><th className="text-end">Rem</th>
                                                <th>Line Status</th><th>Overall</th><th>Ship To</th><th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => {
                                                const epoNum = pick(r, "EpoNumber", "epoNumber");
                                                return (
                                                    <tr key={`${epoNum}-${pick(r, "LineNum", "lineNum")}-${i}`}>
                                                        <td className="fw-semibold">{epoNum}</td>
                                                        <td>{fmtDate(pick(r, "CreatedDate", "createdDate"))}</td>
                                                        <td>{pick(r, "CreatedByName", "createdByName") || pick(r, "CreatedBy", "createdBy") || "—"}</td>
                                                        <td>{pick(r, "Company", "company") || "—"}</td>
                                                        <td>{pick(r, "Description", "description") || "—"}</td>
                                                        <td className="text-end">{pick(r, "QtyOrdered", "qtyOrdered") ?? "—"}</td>
                                                        <td className="text-end">{pick(r, "QtyReceived", "qtyReceived") ?? 0}</td>
                                                        <td className="text-end">{pick(r, "QtyRemaining", "qtyRemaining") ?? "—"}</td>
                                                        <td>{lineStatusBadge(pick(r, "LineStatus", "lineStatus"))}</td>
                                                        <td>{lineStatusBadge(pick(r, "OverallReceivingStatus", "overallReceivingStatus"))}</td>
                                                        <td style={{ maxWidth: 220 }} className="text-truncate">{pick(r, "ShipTo", "shipTo") || "—"}</td>
                                                        <td className="text-nowrap">
                                                            <Button size="sm" color="link" className="p-0 me-2"
                                                                onClick={() => epoReview.open(epoNum, { tab: "basic" })}>Open</Button>
                                                            <Button size="sm" color="link" className="p-0"
                                                                onClick={() => epoReview.open(epoNum, { tab: "lines" })}>Receive</Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pager */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span className="text-muted small">Page {page} of {totalPages}</span>
                                    <div className="d-flex gap-2">
                                        <Button size="sm" color="light" disabled={loading || page <= 1} onClick={() => runSearch(page - 1)}>Previous</Button>
                                        <Button size="sm" color="light" disabled={loading || page >= totalPages} onClick={() => runSearch(page + 1)}>Next</Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>

                {epoReview.element}
            </Container>
        </div>
    );
};

export default EpoLineSearch;
